import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Star } from "lucide-react";
import { TspCard, TspTable, TspBadge, TspProgressBar, TspBreadcrumb, TspEmptyState } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspApiService from "../../api/tspApiService";
import useTspApiResource, { tspErrorMessage } from "../../hooks/useTspApiResource";
import useTspJourneyVisitPicker from "../../hooks/useTspJourneyVisitPicker";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import notification from "../../../../helpers/notification";
import { formatDate, formatNumber } from "../../utils/tspFormat";
import { RESPONSE_RATING_LABEL, RESPONSE_RATING_TONE, RESPONSE_RATINGS } from "../../constants/tspOperations";
import PublicResponseFormCard from "./components/PublicResponseFormCard";
import TspBackButton from "../../components/common/TspBackButton";

export default function TspPublicResponsePage() {
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.PUBLIC_RESPONSE_MANAGE);
  const list = useTspApiResource(() => TspApiService.listPublicResponses({ limit: 50 }), []);
  const summary = useTspApiResource(() => TspApiService.getPublicResponseSummary(), []);
  const picker = useTspJourneyVisitPicker();

  const handleAdd = async (body) => {
    try {
      await TspApiService.createPublicResponse(body);
      notification("success", "Public response recorded.");
      list.reload();
      summary.reload();
      return { ok: true };
    } catch (error) {
      const message = tspErrorMessage(error, "Could not save this response.");
      notification("error", message);
      return { ok: false, message };
    }
  };

  const columns = [
    { key: "date", label: "Date", render: (row) => formatDate(row.created_at.slice(0, 10)) },
    { key: "journey", label: "Journey", render: (row) => row.journey.journey_name },
    { key: "city", label: "City", render: (row) => row.city_visit.location?.label || "—" },
    { key: "response", label: "Response", render: (row) => <TspBadge tone={RESPONSE_RATING_TONE[row.response_rating]}>{RESPONSE_RATING_LABEL[row.response_rating]}</TspBadge> },
    { key: "remarks", label: "Remarks", render: (row) => row.remarks || "—" },
  ];

  const renderEntries = () => {
    if (list.error) return <TspLoadError error={list.error} onRetry={list.reload} what="public response entries" />;
    if (!list.data) return <div className="tsp-dashboard-loading">Loading…</div>;
    if (list.data.length === 0) return <TspEmptyState icon={Star} title="No public response recorded yet" message="Record responses while a journey is in progress." />;
    return <TspTable columns={columns} rows={list.data} rowKey="id" />;
  };

  const breakdown = summary.data?.breakdown || RESPONSE_RATINGS.map((r) => ({ response_rating: r.value, percent: 0 }));

  return (
    <div className="tsp-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Public Response</h1>
          <TspBreadcrumb items={["Dashboard", "Public Response"]} />
        </div>
        <TspBackButton fallback="/tsp" />
      </div>

      {canManage && (
        <PublicResponseFormCard
          journeys={picker.journeys.data || []}
          journeyId={picker.journeyId}
          onJourneyChange={picker.setJourneyId}
          visits={picker.visits.data || []}
          visitId={picker.visitId}
          onVisitChange={picker.setVisitId}
          visitsLoading={picker.visits.loading}
          onSubmit={handleAdd}
        />
      )}

      <div className="tsp-grid tsp-grid-2-1">
        <TspCard title="Response Entries">{renderEntries()}</TspCard>

        <TspCard title="Overall Breakdown" action={summary.data && <span className="tsp-muted-text">{formatNumber(summary.data.total)} total</span>}>
          {summary.error ? (
            <TspLoadError error={summary.error} onRetry={summary.reload} what="the breakdown" />
          ) : (
            RESPONSE_RATINGS.map((r) => {
              const row = breakdown.find((b) => b.response_rating === r.value);
              return <TspProgressBar key={r.value} label={r.label} value={`${row?.percent ?? 0}%`} percent={row?.percent ?? 0} color={r.color} />;
            })
          )}
        </TspCard>
      </div>

      <ToastContainer />
    </div>
  );
}
